import { useCallback, useEffect, useRef, useState } from 'react';
import type { PhotoFileRejection, PhotoFileRejectionReason } from '../atoms/PhotoUploadDropzone/photoFileValidation';

/** At most this many uploads of one queue run at the same time. */
export const PHOTO_UPLOAD_CONCURRENCY = 3;

export type PhotoUploadStatus = 'queued' | 'uploading' | 'done' | 'failed' | 'rejected';

export interface PhotoUploadItem {
  id: string;
  file: File;
  status: PhotoUploadStatus;
  /** 0–100 while uploading. */
  progress: number;
  /** Set for status 'rejected'. */
  rejection?: PhotoFileRejectionReason;
}

export type PhotoUploadAnnouncement =
  | { kind: 'queued'; queued: number; rejected: number }
  | { kind: 'finished'; done: number; failed: number };

export interface PhotoUploadQueueOptions {
  /** Uploads one file; rejects on failure (the item can then be retried). */
  upload: (file: File, onProgress: (percent: number) => void) => Promise<void>;
  /** Called once a batch has finished (nothing queued or uploading any more). */
  onBatchFinished?: (result: { done: number; failed: number }) => void;
  /** Prefix of the item ids (unique per surface, e.g. "gallery-upload"). */
  idPrefix?: string;
  concurrency?: number;
}

let nextId = 0;

/**
 * Per-file upload queue shared by the gallery and the livestock photos: one
 * request per file (each file has its own progress and can be retried on its
 * own), at most `concurrency` at a time, a summary announcement per batch.
 */
export function usePhotoUploadQueue({
  upload,
  onBatchFinished,
  idPrefix = 'photo-upload',
  concurrency = PHOTO_UPLOAD_CONCURRENCY,
}: PhotoUploadQueueOptions) {
  const [items, setItems] = useState<PhotoUploadItem[]>([]);
  const [announcement, setAnnouncement] = useState<PhotoUploadAnnouncement | null>(null);
  const startedRef = useRef(new Set<string>());
  const batchRef = useRef({ done: 0, failed: 0 });
  const mountedRef = useRef(true);
  // The latest callbacks, so a running upload never calls a stale closure.
  const uploadRef = useRef(upload);
  const onBatchFinishedRef = useRef(onBatchFinished);
  uploadRef.current = upload;
  onBatchFinishedRef.current = onBatchFinished;

  const makeId = useCallback(() => {
    nextId += 1;
    return `${idPrefix}-${nextId}`;
  }, [idPrefix]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const patch = useCallback((id: string, changes: Partial<PhotoUploadItem>) => {
    if (!mountedRef.current) return;
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  }, []);

  const runUpload = useCallback(
    async (item: PhotoUploadItem) => {
      try {
        await uploadRef.current(item.file, (percent) => {
          patch(item.id, { progress: percent });
        });
        batchRef.current.done += 1;
        patch(item.id, { status: 'done', progress: 100 });
      } catch {
        batchRef.current.failed += 1;
        patch(item.id, { status: 'failed' });
      } finally {
        startedRef.current.delete(item.id);
      }
    },
    [patch],
  );

  // Scheduler: start queued items while fewer than the limit are uploading.
  useEffect(() => {
    const active = items.filter((i) => i.status === 'uploading').length;
    const free = concurrency - active;
    if (free <= 0) return;
    const toStart = items
      .filter((i) => i.status === 'queued' && !startedRef.current.has(i.id))
      .slice(0, free);
    if (toStart.length === 0) return;
    const ids = new Set(toStart.map((i) => i.id));
    toStart.forEach((i) => startedRef.current.add(i.id));
    setItems((prev) =>
      prev.map((i) => (ids.has(i.id) ? { ...i, status: 'uploading', progress: 0 } : i)),
    );
    toStart.forEach((i) => {
      void runUpload(i);
    });
  }, [items, runUpload, concurrency]);

  // Batch completion: report and announce the result once nothing is pending.
  const busy = items.some((i) => i.status === 'queued' || i.status === 'uploading');
  const wasBusyRef = useRef(false);
  useEffect(() => {
    if (busy) {
      wasBusyRef.current = true;
      return;
    }
    if (!wasBusyRef.current) return;
    wasBusyRef.current = false;
    const result = batchRef.current;
    batchRef.current = { done: 0, failed: 0 };
    onBatchFinishedRef.current?.(result);
    setAnnouncement({ kind: 'finished', done: result.done, failed: result.failed });
  }, [busy]);

  const addFiles = useCallback(
    (accepted: File[], rejected: PhotoFileRejection[]) => {
      if (accepted.length === 0 && rejected.length === 0) return;
      const queued: PhotoUploadItem[] = accepted.map((file) => ({
        id: makeId(),
        file,
        status: 'queued',
        progress: 0,
      }));
      const refused: PhotoUploadItem[] = rejected.map(({ file, reason }) => ({
        id: makeId(),
        file,
        status: 'rejected',
        progress: 0,
        rejection: reason,
      }));
      setItems((prev) => [...prev, ...queued, ...refused]);
      setAnnouncement({ kind: 'queued', queued: queued.length, rejected: refused.length });
    },
    [makeId],
  );

  const retry = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id && i.status === 'failed' ? { ...i, status: 'queued', progress: 0 } : i)),
    );
  }, []);

  /** Removes finished, failed and rejected entries; running uploads stay. */
  const clearFinished = useCallback(() => {
    setItems((prev) => prev.filter((i) => i.status === 'queued' || i.status === 'uploading'));
  }, []);

  /** Removes every entry (e.g. when the surface is reset); running uploads finish in the background. */
  const reset = useCallback(() => {
    setItems([]);
    setAnnouncement(null);
  }, []);

  return { items, busy, announcement, addFiles, retry, clearFinished, reset };
}
